import { useMutation, useSuspenseQuery } from '@apollo/client'
import { OAUTH_ERROR_CODES } from '@salescore/buff-common'
import {
  CreateOauthI18nSourceDocument,
  CreateOauthSourceDocument,
  FetchMeDocument,
  type LangEnum,
  SourceProviderEnum,
} from '@salescore/client-api'
import {
  CLIENT_COMMON_CONSTANT,
  getOrganizationIdFromPath,
  HandleQuery,
  LATEST_OPENED_EXTRA_CONFIG,
  LATEST_OPENED_I18N_LANG,
  LATEST_OPENED_SOURCE_KEY,
  type OAuthExtraConfig,
  oauthExtraConfigSchema,
  ProviderLogo,
} from '@salescore/client-common'
import { parseJsonIfValid } from '@salescore/frontend-common'
import { Input, message, Modal, Result } from 'antd'
import Form from 'antd/es/form/Form'
import FormItem from 'antd/es/form/FormItem'
import { t } from 'i18next'
import { type ReactElement, useState } from 'react'
import { z } from 'zod'

import { SourceConfigForm } from './CreateSourceConfirmModal/SourceConfigForm'

const langSchema = z.enum(['en', 'ja'])

const langToLabel: Record<LangEnum, string> = {
  en: t(`英語`),
  ja: t(`日本語`),
}

interface Source {
  id: string
  name: string
  provider: SourceProviderEnum
}

const getLatestOpenedSourceId = (provider: string): Source | undefined => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const latestOpenedSource = JSON.parse(localStorage.getItem(LATEST_OPENED_SOURCE_KEY) ?? '{}')

    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: parseすべき
    if (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      latestOpenedSource.provider === provider &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      latestOpenedSource.id !== undefined &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      latestOpenedSource.id !== null &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (typeof latestOpenedSource.id === 'string'
        ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          latestOpenedSource.id !== ''
        : // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          !Array.isArray(latestOpenedSource.id) || latestOpenedSource.id.length > 0)
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return latestOpenedSource
    }

    return undefined
  } catch {
    return undefined
  }
}

export const CreateSourceConfirmModal = ({
  oauthCode,
  state,
  provider,
  redirectToConnection,
}: {
  oauthCode: string | undefined
  state: string | undefined
  provider: string | undefined
  redirectToConnection: (sourceId: string) => void
}): ReactElement => {
  const [sourceConfig, setSourceConfig] = useState<Record<string, unknown>>({})
  const [createOauthSourceMutation] = useMutation(CreateOauthSourceDocument)
  const [createOauthI18nSourceMutation] = useMutation(CreateOauthI18nSourceDocument)
  const isValidParameter =
    oauthCode !== undefined &&
    state !== undefined &&
    provider !== undefined &&
    oauthCode !== '' &&
    state !== '' &&
    provider !== ''
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const latestOpenedSource = getLatestOpenedSourceId(provider!)
  const [visibility, setVisibility] = useState(isValidParameter)
  const [sourceName, setSourceName] = useState(latestOpenedSource?.name ?? '')
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const tokenUrl = getTokenUrl(provider as SourceProviderEnum)
  const extraConfig = getExtraConfig()
  const [i18nLang] = useState(() => {
    const lang = localStorage.getItem(LATEST_OPENED_I18N_LANG)
    const validated = langSchema.safeParse(lang)
    return validated.success ? validated.data : undefined
  })
  const shouldCreateI18nSource = i18nLang !== undefined && latestOpenedSource !== undefined

  const createOauthI18nSourceFromQueryParameter = async (): Promise<void> => {
    setLoading(true)
    if (!shouldCreateI18nSource) {
      // この分岐に来る時は、この条件はありえないはず
      return
    }
    await createOauthI18nSourceMutation({
      variables: {
        input: {
          organizationId: getOrganizationIdFromPath(),
          sourceId: latestOpenedSource.id,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          code: oauthCode!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          state: state!,
          lang: i18nLang,
          name: sourceName,
        },
      },
      onCompleted: () => {
        localStorage.removeItem(LATEST_OPENED_EXTRA_CONFIG)
        localStorage.removeItem(LATEST_OPENED_I18N_LANG)
        void message.success(t(`言語設定用の連携を追加しました`))
        // eslint-disable-next-line sonarjs/todo-tag
        // TODO: ユーザーリロードに備えてqueryのリセットをしたい。リロードを走らせずに変更する方法が分からなかったので一旦なし
        setVisibility(false)
      },
      onError: (error) => {
        if (
          error.graphQLErrors.some(
            (gError) => gError.extensions?.code === OAUTH_ERROR_CODES.OAUTH_STATE_MISMATCH_OR_EXPIRED,
          )
        ) {
          setErrorMessage(t('操作がタイムアウトしました。再度お試しください。'))
          return
        }
        void message.error(t(`言語設定用の連携が失敗しました`))
        if (error instanceof Error) {
          setErrorMessage(error.message)
        }
      },
    })
    setLoading(false)
  }

  const createOauthSourceFromQueryParameter = async (): Promise<void> => {
    setLoading(true)
    await createOauthSourceMutation({
      variables: {
        oauthSourceInput: {
          id: latestOpenedSource?.id,
          organizationId: getOrganizationIdFromPath(),

          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          code: oauthCode!,

          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          state: state!,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          provider: provider as SourceProviderEnum,
          sourceConfig,
          name: sourceName,
          tokenUrl,
          clientId: extraConfig?.clientId,
          clientSecret: extraConfig?.clientSecret,
        },
      },
      onCompleted: (data) => {
        localStorage.removeItem(LATEST_OPENED_EXTRA_CONFIG)
        void message.success(t(`連携を追加しました`))
        // eslint-disable-next-line sonarjs/todo-tag
        // TODO: ユーザーリロードに備えてqueryのリセットをしたい。リロードを走らせずに変更する方法が分からなかったので一旦なし
        setVisibility(false)
        if (data.createOauthSourceV2?.id !== undefined) {
          void message.success(t(`同期項目の設定をお願いします`))
          redirectToConnection(data.createOauthSourceV2.id)
        }
      },
      onError: (error) => {
        if (
          error.graphQLErrors.some(
            (gError) => gError.extensions?.code === OAUTH_ERROR_CODES.OAUTH_STATE_MISMATCH_OR_EXPIRED,
          )
        ) {
          const message_ = t('操作がタイムアウトしました。再度お試しください。')
          void message.error(message_)
          setErrorMessage(message_)
          return
        }
        if (error.graphQLErrors.some((gError) => gError.extensions?.code === 'CONNECTED_SFA')) {
          void message.error(t('連携済みのため設定できません。'))
          setErrorMessage(t('複数の連携ができない連携先です。既に連携済みのため設定できませんでした。'))
          return
        }
        void message.error(t(`連携に失敗しました`))
        setErrorMessage(error.message)
      },
    })
    setLoading(false)
  }

  const organizationId = getOrganizationIdFromPath()
  const {
    data: { myUser },
  } = useSuspenseQuery(FetchMeDocument, {
    variables: { organizationId },
    fetchPolicy: 'cache-first',
  })

  return (
    <HandleQuery>
      <Modal
        key="createModal"
        open={visibility}
        onCancel={() => {
          setVisibility(false)
        }}
        width={600}
        okButtonProps={{ style: { display: 'none' } }}
        cancelButtonProps={{ style: { display: 'none' } }}
        style={{ top: '3%' }}
      >
        {provider !== undefined && (
          <Result
            icon={<ProviderLogo provider={provider} />}
            title={
              <div>
                {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                  CLIENT_COMMON_CONSTANT.i18n.ja.provider[provider as SourceProviderEnum]
                }
                {shouldCreateI18nSource
                  ? t(`の{{lang}}設定用の連携を行います`, { lang: langToLabel[i18nLang] })
                  : // eslint-disable-next-line sonarjs/no-nested-conditional
                    latestOpenedSource?.id === undefined
                    ? t('と連携します')
                    : t('を再連携します')}
                <br />
                {t(`よろしいですか？`)}
              </div>
            }
            subTitle={`${t(`ログイン中の組織`)}：${myUser.organization.name}`}
            extra={
              errorMessage === undefined
                ? [
                    <Form layout="vertical" key={'form'}>
                      {!shouldCreateI18nSource && (
                        <FormItem label={t(`(任意)連携先の名前`)} required={false}>
                          <Input
                            value={sourceName}
                            onChange={(event) => {
                              setSourceName(event.target.value)
                            }}
                          />
                        </FormItem>
                      )}
                      <div>
                        {tokenUrl !== undefined && (
                          <FormItem label={'URL'} required={false}>
                            <Input value={tokenUrl} readOnly disabled />
                          </FormItem>
                        )}
                        {extraConfig?.clientId !== undefined && (
                          <FormItem label={'URL'} required={false}>
                            <Input value={extraConfig.clientId} readOnly disabled />
                          </FormItem>
                        )}
                      </div>
                    </Form>,
                    <SourceConfigForm
                      key={'source_config_form'}
                      // eslint-disable-next-line sonarjs/todo-tag
                      // TODO validation
                      provider={
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                        provider as SourceProviderEnum
                      }
                      loading={loading}
                      onSave={async () => {
                        await (shouldCreateI18nSource
                          ? createOauthI18nSourceFromQueryParameter()
                          : createOauthSourceFromQueryParameter())
                      }}
                      setSourceConfig={setSourceConfig}
                      sourceConfig={sourceConfig}
                    />,
                    // <Button
                    //   onClick={() => {
                    //     setVisibility(false)
                    //   }}
                    // >
                    //   キャンセル
                    // </Button>,
                  ]
                : [
                    <div className="text-red-600" key={'error_message'}>
                      {errorMessage}
                    </div>,
                  ]
            }
          />
        )}
      </Modal>
    </HandleQuery>
  )
}

function getTokenUrl(provider: SourceProviderEnum): string | undefined {
  if (provider === SourceProviderEnum.Salesforce) {
    const extraConfig = getExtraConfig()
    if (extraConfig === undefined) {
      return undefined
    }
    const { authorizeUrl } = extraConfig

    if (
      authorizeUrl === undefined ||
      !authorizeUrl.startsWith(`https://`) ||
      !authorizeUrl.endsWith(`/services/oauth2/authorize`)
    ) {
      return undefined
    }
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: このロジックがクライアントベタかつ、サーバーサイドのcreateOauthUserSource側でも行っているのがよくない
    return authorizeUrl.replace(`/services/oauth2/authorize`, `/services/oauth2/token`)
  }
  return undefined
}

function getExtraConfig(): OAuthExtraConfig | undefined {
  const extraConfigJson = localStorage.getItem(LATEST_OPENED_EXTRA_CONFIG)
  const validated = oauthExtraConfigSchema.safeParse(parseJsonIfValid(extraConfigJson ?? ''))
  if (!validated.success) {
    return
  }
  return validated.data
}
